import React, { useEffect } from 'react';
import { useBrandTagsQuery, useClientTagsQuery, useClientTypeQuery, useRegionsListQuery } from 'api';
import { CalendarIcon } from 'components/common/icons';
import styles from 'pages/admin/clients/styles.module.scss';
import { ClientSelect } from 'components/common/clientSelect';
import { CountrySelect } from 'components/common/countrySelect';
import {
  AdminSelect,
  Col,
  DatePicker,
  FilterDrawer,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  TagSelect,
} from 'components';
import { useFilters } from './useFilters';
import { OrderStatusType, OrderStatusTypes } from 'models/Order';

const { RangePicker } = DatePicker;

interface IFilters {
  form: FormInstance;
  visible: boolean;
  onClose: () => void;
  onApply: (value: Record<string, any>) => void;
  onReset: () => void;
  statusFilter?: OrderStatusType | 'ALL';
}

export const Filters = React.memo((props: IFilters) => {
  const { form, visible, onClose, onApply, onReset, statusFilter } = props;
  const { data: regions = [] } = useRegionsListQuery();
  const { data: clientTypeData = [] } = useClientTypeQuery();
  const { data: brandTagData = [] } = useBrandTagsQuery();
  const { data: clientTagData = [] } = useClientTagsQuery();

  const { clearFilters } = useFilters({ form, resetFilters: onReset });

  // Sync status filter with form when statusFilter prop changes
  useEffect(() => {
    if (statusFilter && statusFilter !== 'ALL') {
      form.setFieldValue('status', statusFilter);
    } else if (statusFilter === 'ALL') {
      form.setFieldValue('status', undefined);
    }
  }, [statusFilter, form]);

  // Handle form submission and transform single status to array
  const handleFormSubmit = (values: any) => {
    const transformedValues = {
      ...values,
      // Convert single status to array format for API compatibility
      status: values.status ? [values.status] : undefined,
    };
    onApply(transformedValues);
  };

  return (
    <FilterDrawer
      title="Filter"
      placement="left"
      width={471}
      styles={{
        body: {
          marginBottom: 80,
        },
      }} // Add space for drawer actions
      onClose={onClose}
      open={visible}
      onApply={() => form.submit()}
      onClear={clearFilters}
      onExpand={onClose}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFormSubmit}
        initialValues={{
          clientId: null,
          countryId: null,
          region: null,
          status: statusFilter && statusFilter !== 'ALL' ? statusFilter : undefined,
          createdDate: [null, null],
          lastUpdatedDate: [null, null],
        }}
      >
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Form.Item name="clientId" label="Client Name">
              <ClientSelect allowClear />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="region" label="Region">
              <Select mode={'multiple'} maxTagCount={'responsive'} options={regions} allowClear />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="countryId" label="Delivery Country">
              <CountrySelect allowClear />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="assignedRoleId" label="Assigned Roles">
              <AdminSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="minPriceRange" label={'Price Range'}>
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxPriceRange">
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="minClientConversionRate" label={'Client Conversion Rate'}>
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxClientConversionRate">
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="minBrandConversionRate" label={'Brand Conversion Rate'}>
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxBrandConversionRate">
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <TagSelect data={clientTagData} size={'middle'} name={'clientTags'} label={'Client Tags'} />
          </Col>
          <Col span={24}>
            <TagSelect data={brandTagData} size={'middle'} name={'brandTags'} label={'Brand Tags'} />
          </Col>
          <Col span={24}>
            <Form.Item name="clientTypeId" label="Client type">
              <Select options={clientTypeData} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="status" label="Status">
              <Select options={OrderStatusTypes} allowClear placeholder="Select status" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="createdDate" label="Created Date">
              <RangePicker
                format="DD.MM.YYYY"
                suffixIcon={<CalendarIcon />}
                className={styles.range_picker}
                popupClassName="fmg-range-picker"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="lastUpdatedDate" label="Last Updated">
              <RangePicker
                format="DD.MM.YYYY"
                suffixIcon={<CalendarIcon />}
                className={styles.range_picker}
                popupClassName="fmg-range-picker"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
});
